import React, { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { Formik, Form, Field } from "formik";
import { useCreateCustomer } from "hooks/useCustomers";
import { customerValidationSchema } from "utils/rules/customer";
import { useGetBranches } from "hooks/useBranches";

const AddCustomerModal = ({ isOpen, toggle }) => {
  const { mutate: createCustomer, isPending } = useCreateCustomer();
  const { data: branches, isLoading: loadingBranches } = useGetBranches();
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState<string>("");

  const initialValues = {
    name: "",
    branch_id: "",
    email: "",
    phone: "",
    notes: "",
    tags: "",
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (tagInput.trim() && !tags.includes(tagInput.trim())) {
        setTags([...tags, tagInput.trim()]);
        setTagInput("");
      }
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleSubmit = (values, { resetForm }) => {
    values.tags = tags.length > 0 ? tags.join(",") : "";
    if (tagInput.trim()) {
      values.tags = values.tags
        ? `${values.tags}, ${tagInput.trim()}`
        : tagInput.trim();
    }
    // console.log("Tag input: ", tagInput);

    createCustomer(
      { ...values, tags: values.tags },
      {
        onSuccess: () => {
          resetForm();
          setTags([]);
          toggle();
          setTagInput("");
        },
      }
    );
  };

  const handleCancelClick = () => {
    setTags([]);
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleCancelClick} centered={true}>
      <ModalHeader toggle={handleCancelClick}>Add New Customer</ModalHeader>
      <ModalBody>
        <Formik
          initialValues={initialValues}
          validationSchema={customerValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, setFieldValue, values }) => {
            return (
              <Form>
                <FormGroup>
                  <Label for="name">Name</Label>
                  <Field name="name">
                    {({ field }) => (
                      <Input
                        id="name"
                        {...field}
                        placeholder="Enter customer name"
                        required
                        invalid={errors.name && touched.name}
                      />
                    )}
                  </Field>
                </FormGroup>
                <FormGroup>
                  <Label for="branch_id">Branch</Label>
                  <Field name="branch_id">
                    {({ field }) => (
                      <Input
                        id="branch_id"
                        type="select"
                        {...field}
                        required
                        invalid={errors.branch_id && touched.branch_id}
                      >
                        {loadingBranches ? (
                          <option disabled>Loading branches...</option>
                        ) : (
                          <>
                            <option value="">Select a branch</option>
                            {branches?.map((branch) => (
                              <option key={branch.id} value={branch.id}>
                                {branch.name}
                              </option>
                            ))}
                          </>
                        )}
                      </Input>
                    )}
                  </Field>
                </FormGroup>
                <FormGroup>
                  <Label for="email">Email</Label>
                  <Field name="email">
                    {({ field }) => (
                      <Input
                        id="email"
                        type="email"
                        {...field}
                        placeholder="Enter customer email"
                        invalid={errors.email && touched.email}
                      />
                    )}
                  </Field>
                </FormGroup>
                <FormGroup>
                  <Label for="phone">Phone</Label>
                  <Field name="phone">
                    {({ field }) => (
                      <Input
                        id="phone"
                        {...field}
                        placeholder="Enter customer phone"
                        invalid={errors.phone && touched.phone}
                      />
                    )}
                  </Field>
                </FormGroup>
                <FormGroup>
                  <Label for="notes">Notes</Label>
                  <Field name="notes">
                    {({ field }) => (
                      <Input
                        id="notes"
                        type="textarea"
                        {...field}
                        placeholder="Enter notes"
                        invalid={errors.notes && touched.notes}
                      />
                    )}
                  </Field>
                </FormGroup>
                <FormGroup>
                  <Label for="tags">Tags</Label>
                  <div
                    style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}
                  >
                    {tags.map((tag, index) => (
                      <span key={index} className="tag-pill">
                        {tag}
                        <button type="button" onClick={() => removeTag(tag)}>
                          x
                        </button>
                      </span>
                    ))}
                    <Input
                      type="text"
                      value={tagInput}
                      onChange={(e) => {
                        setTagInput(e.target.value);
                      }}
                      placeholder="Add a tag and press Enter"
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </FormGroup>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button color="primary" type="submit" disabled={isPending}>
                    {isPending ? "Adding..." : "Add Customer"}
                  </Button>
                  <Button
                    color="secondary"
                    onClick={handleCancelClick}
                    style={{ marginLeft: "10px" }}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </ModalBody>
    </Modal>
  );
};

export default AddCustomerModal;
